import { useState, useEffect } from 'react';
import type { Product } from '@/types/product';
import { supabase } from '@/integrations/supabase/client';
import { useCartDatabase } from './useCartDatabase';
import { useCartProducts } from './useCartProducts';

export type CartItem = {
  id: string;
  quantity: number;
  product: Product;
};

const readGuestIds = (): string[] => {
  try {
    const raw = localStorage.getItem('cart');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.map((item: any) => String(item.id)).filter(Boolean)
      : [];
  } catch {
    return [];
  }
};

export const useCartState = () => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [cartProductIds, setCartProductIds] = useState<string[]>(readGuestIds);
  const [isBootstrapped, setIsBootstrapped] = useState(false);

  const { data: products = [] } = useCartProducts(cartProductIds);
  const { syncCartToDatabase, loadCartFromDatabase } = useCartDatabase();

  useEffect(() => {
    console.log('🔄 Setting up auth state listener for cart');

    const initializeAuthState = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        console.log('🔐 Initial auth session:', session?.user?.id);

        if (session?.user?.id) {
          setCurrentUserId(session.user.id);
        } else {
          console.log('👤 No session — guest mode, will load cart from localStorage');
          setCurrentUserId(null);
          setIsBootstrapped(true);
        }
      } catch (error) {
        console.error('❌ Error getting initial session:', error);
        setIsBootstrapped(true);
      }
    };

    initializeAuthState();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔐 Auth state changed:', { event, userId: session?.user?.id });

        if (event === 'SIGNED_OUT' || !session?.user?.id) {
          console.log('🚪 User signed out, clearing local cart state (database cart preserved)');
          setCartItems([]);
          setCurrentUserId(null);
          sessionStorage.removeItem('cart');
          setIsInitialized(false);
          setCartProductIds(readGuestIds());
          setIsBootstrapped(true);
        } else if (event === 'SIGNED_IN' && session?.user?.id) {
          console.log('✅ User signed in:', session.user.id);

          if (currentUserId && currentUserId !== session.user.id) {
            console.log('🔄 Different user detected, clearing previous cart');
            setCartItems([]);
            localStorage.removeItem('cart');
            sessionStorage.removeItem('cart');
          }

          setCurrentUserId(session.user.id);
          setIsBootstrapped(false);
          setIsInitialized(false);
        }
      }
    );

    return () => {
      console.log('🧹 Cleaning up cart auth subscription');
      subscription.unsubscribe();
    };
  }, [currentUserId]);

  // For auth users: fetch product IDs from cart_items (IDs only — lightweight)
  useEffect(() => {
    if (!currentUserId) return;
    const fetchAuthCartIds = async () => {
      const { data, error } = await supabase
        .from('cart_items')
        .select('product_id')
        .eq('user_id', currentUserId);
      if (!error && data) {
        setCartProductIds(data.map(row => row.product_id));
      }
      setIsBootstrapped(true);
    };
    fetchAuthCartIds();
  }, [currentUserId]);

  // Once initialized, keep cartProductIds derived from cartItems (reactive to add/remove)
  useEffect(() => {
    if (isInitialized) {
      setCartProductIds(cartItems.map(item => item.id));
    }
  }, [cartItems, isInitialized]);

  const hydrateLocalCart = (raw: string | null): CartItem[] => {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return parsed
        .map((item: any) => {
          const currentProduct = products.find(p => String(p.id) === String(item.id));
          return currentProduct ? { ...item, product: currentProduct } : null;
        })
        .filter((item: CartItem | null): item is CartItem => item !== null);
    } catch (e) {
      console.error('❌ Failed to parse cart from localStorage:', e);
      localStorage.removeItem('cart');
      return [];
    }
  };

  const mergeCarts = (a: CartItem[], b: CartItem[]): CartItem[] => {
    const map = new Map<string, CartItem>();
    [...a, ...b].forEach(item => {
      const key = String(item.id);
      const existing = map.get(key);
      if (existing) {
        map.set(key, { ...existing, quantity: existing.quantity + item.quantity });
      } else {
        map.set(key, { ...item });
      }
    });
    return Array.from(map.values());
  };

  useEffect(() => {
    const loadCartData = async () => {
      if (!isBootstrapped) return;
      if (cartProductIds.length > 0 && products.length === 0) return;
      if (isInitialized) return;

      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.user) {
          const guestCart = hydrateLocalCart(localStorage.getItem('cart'));
          if (guestCart.length > 0) {
            console.log('👤 Guest cart loaded from localStorage:', guestCart.length, 'items');
            setCartItems(guestCart);
          }
          setIsInitialized(true);
          return;
        }

        console.log('📦 Loading cart for user:', session.user.id);

        const guestCart = hydrateLocalCart(localStorage.getItem('cart'));

        let dbCart: CartItem[] = [];
        try {
          dbCart = await loadCartFromDatabase(products);
        } catch (error) {
          console.error('❌ Error loading cart from database:', error);
        }

        let finalCart: CartItem[];
        if (guestCart.length > 0) {
          console.log('🔀 Merging guest cart into user cart:', { guest: guestCart.length, db: dbCart.length });
          finalCart = mergeCarts(dbCart, guestCart);
          await syncCartToDatabase(finalCart);
          localStorage.removeItem('cart');
        } else {
          finalCart = dbCart;
          localStorage.removeItem('cart');
        }

        setCartItems(finalCart);
        console.log('✅ Cart loaded:', finalCart.length, 'items');
        setIsInitialized(true);
      } catch (error) {
        console.error('Error loading cart data:', error);
        setIsInitialized(true);
      }
    };

    loadCartData();
  }, [products.length, isInitialized, isBootstrapped]);

  useEffect(() => {
    const saveCartData = async () => {
      if (!isInitialized) return;

      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        if (cartItems.length > 0) {
          localStorage.setItem('cart', JSON.stringify(cartItems));
        } else {
          localStorage.removeItem('cart');
        }
        console.log('👤 Guest cart saved to localStorage only:', cartItems.length, 'items');
        return;
      }

      localStorage.removeItem('cart');
      console.log('💾 Saving cart for user:', session.user.id, 'Items:', cartItems.length);
      await syncCartToDatabase(cartItems);
    };

    saveCartData();
  }, [cartItems, isInitialized]);

  return {
    cartItems,
    setCartItems,
    isInitialized,
    products,
  };
};
